import { useState, useEffect, FocusEvent } from "react";
import { formatNumericValue, parseNumericInput, clampValue } from "./utils";

interface UseNumericInputStateProps {
  value: number;
  onChange: (value: number) => void;
  formatValue?: (value: number) => string;
  parseValue?: (input: string) => number;
  prefix?: string;
  suffix?: string;
  min?: number;
  max?: number;
  allowNegative?: boolean;
  decimalPlaces?: number;
}

export function useNumericInputState({
  value,
  onChange,
  formatValue,
  parseValue,
  prefix = "",
  suffix = "",
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  allowNegative = false,
  decimalPlaces
}: UseNumericInputStateProps) {
  // State to track the input string during typing
  const [inputValue, setInputValue] = useState<string>("");
  // Track if user is currently inputting (to prevent formatting during entry)
  const [isUserTyping, setIsUserTyping] = useState<boolean>(false);
  // Track if selection is the entire field
  const [hadFullSelection, setHadFullSelection] = useState<boolean>(false);
  // Track the last non-empty user input to recover from invalid states
  const [lastValidUserInput, setLastValidUserInput] = useState<string>("");
  // Add animation state
  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  // Effect to format the value when it changes externally
  useEffect(() => {
    if (!isUserTyping) {
      const formattedValue = formatNumericValue(value, {
        formatValue,
        decimalPlaces,
        prefix,
        suffix
      });
      
      setInputValue(formattedValue);
      setLastValidUserInput(formattedValue);
    }
  }, [value, formatValue, suffix, prefix, decimalPlaces, isUserTyping]);

  // Handle selection detection on mouseup/keyup
  const handleSelectionCheck = (e: React.MouseEvent | React.KeyboardEvent) => {
    const input = e.target as HTMLInputElement;
    // Check if the entire content is selected
    const isFullySelected = 
      input.selectionStart === 0 && 
      input.selectionEnd === input.value.length;
    
    if (isFullySelected) {
      setHadFullSelection(true);
    }
  };

  // Handle input change with enhanced animations
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setIsUserTyping(true);
    
    // Add subtle animation on change
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);
    
    // Always update the displayed value during typing
    setInputValue(input);
    
    // Handle case where user completely deletes the input
    if (input === "" || input === prefix || input === suffix) {
      // Don't update the model yet, wait for blur to decide if we reset or keep the last value
      return;
    }
    
    // Special handling for full field selection replacement
    if (hadFullSelection) {
      // User likely replaced entire content with a new number
      // If it's reasonable length, save it even if parsing would fail for now
      if (input.length <= 15) {
        setLastValidUserInput(input);
        // If the input is a simple number, parse it now, otherwise wait for blur
        if (/^[0-9]+$/.test(input) || /^[0-9]+\.[0-9]*$/.test(input)) {
          try {
            const parsedValue = parseFloat(input);
            if (!isNaN(parsedValue)) {
              const clampedValue = clampValue(parsedValue, min, max);
              onChange(clampedValue);
            }
          } catch (error) {
            // Ignore parsing errors while typing
          }
        }
        return;
      }
      setHadFullSelection(false);
    }
    
    // Try to parse and update the actual value if valid
    try {
      const parsedValue = parseNumericInput(input, {
        parseValue,
        prefix,
        suffix,
        allowNegative
      });
      
      if (!isNaN(parsedValue)) {
        // Save this as a valid user input
        setLastValidUserInput(input);
        
        // Don't allow values outside the range
        const clampedValue = clampValue(parsedValue, min, max);
        
        if (parsedValue !== value) {
          onChange(clampedValue);
        }
      }
    } catch (error) {
      console.error("Error parsing numeric input:", error);
    }
  };

  // Handle blur to finalize the formatting
  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    let finalValue = value;
    setIsUserTyping(false);
    setHadFullSelection(false);
    
    // Add completion animation
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);
    
    // Try one final parse of the displayed input
    try {
      const parsedValue = parseNumericInput(inputValue, {
        parseValue,
        prefix, 
        suffix,
        allowNegative
      });
      
      if (!isNaN(parsedValue)) {
        finalValue = parsedValue;
      } else if (lastValidUserInput) {
        // If current parse failed but we have a previous valid input, try parsing that
        const lastValidParsed = parseNumericInput(lastValidUserInput, {
          parseValue,
          prefix,
          suffix,
          allowNegative
        });
        
        if (!isNaN(lastValidParsed)) {
          finalValue = lastValidParsed;
        }
      }
    } catch (error) {
      // If parsing fails, try using the last valid input
      try {
        if (lastValidUserInput) {
          const lastValidParsed = parseNumericInput(lastValidUserInput, {
            parseValue,
            prefix,
            suffix,
            allowNegative
          });
          
          if (!isNaN(lastValidParsed)) {
            finalValue = lastValidParsed;
          }
        }
      } catch (e) {
        // If all parsing attempts fail, use the current value
        console.error("Error on final parse:", error);
      }
    }
    
    // Ensure value is within range
    finalValue = clampValue(finalValue, min, max);
    
    // Update the stored value
    onChange(finalValue);
    
    // Format the input display
    const formattedValue = formatNumericValue(finalValue, {
      formatValue,
      decimalPlaces,
      prefix,
      suffix
    });
    
    setInputValue(formattedValue);
    setLastValidUserInput(formattedValue);
  };

  // Handle slider change with animations
  const handleSliderChange = (newValue: number[]) => {
    const singleValue = newValue[0];
    onChange(singleValue);
    
    // Add slider animation
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);
    
    // Also update the input display
    const formattedValue = formatNumericValue(singleValue, {
      formatValue,
      decimalPlaces,
      prefix,
      suffix
    });
    
    setInputValue(formattedValue);
    setLastValidUserInput(formattedValue);
  };

  return {
    inputValue,
    isAnimating,
    handleInputChange,
    handleBlur,
    handleSelectionCheck,
    handleSliderChange
  };
}
